import React from "react";
import styled from "styled-components";
import { useDrop } from "react-dnd";
import { useTranslation } from "react-i18next";
import PuzzlePiece from "./PuzzlePiece";
import PuzzleTarget from "./PuzzleTarget";

const BoardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  padding: 1.5rem;
  width: 100%;
  max-width: 900px;
  margin: 0 auto; 
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: center;
    align-items: flex-start;
    gap: 3rem;
  }
`;

const PuzzlePiecesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  width: 300px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  transition: background-color 0.2s, box-shadow 0.2s;
  background-color: ${({ isOver }) =>
    isOver ? "rgba(255, 255, 255, 0.2)" : "rgba(255, 255, 255, 0.1)"};
  box-shadow: ${({ isOver }) =>
    isOver
      ? "0 8px 32px rgba(255, 255, 255, 0.2)"
      : "0 8px 32px rgba(0, 0, 0, 0.3)"};
  @media (max-width: 600px) {
    gap: 0px;
    padding: 2px;
  }
  @media (max-width: 400px) {
    gap: 4px;
    padding: 4px;
  }
`;

const PiecesLabel = styled.div`
  width: 100%;
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.2rem;
  font-weight: 500;
  letter-spacing: 1px;
  margin-bottom: 8px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const PuzzleTargetsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 100px);
  gap: 12px;
  padding: 20px;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(8px);
  border-radius: 32px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);

  @media (max-width: 600px) {
    grid-template-columns: repeat(3, 80px);
    gap: 8px;
    padding: 16px;
  }

  @media (max-width: 400px) {
    grid-template-columns: repeat(3, 70px);
    gap: 6px;
    padding: 12px;
  }
`;

const PuzzleBoard = ({
  pieces,
  rows,
  cols,
  placedPieces,
  onDrop,
  onDropToSource,
  onRemovePiece,   // parent-provided removal handler
}) => {
  const { t } = useTranslation();

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: "PIECE",   // ⬅️ FIXED: matches PuzzlePiece drag type
      drop: (item) => {
        if (onDropToSource) {
          onDropToSource(item.piece);  // return piece to source
        }
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
      }),
    }),
    [onDropToSource]
  );

  return (
    <BoardContainer>
      <PuzzlePiecesContainer ref={drop} isOver={isOver}>
        <PiecesLabel>{t('puzzle.piecesLabel')}</PiecesLabel>
        {pieces.map((piece) => (
          <PuzzlePiece key={piece.id} piece={piece} />
        ))}
      </PuzzlePiecesContainer>

      <PuzzleTargetsContainer>
        {[...Array(rows * cols)].map((_, index) => (
          <PuzzleTarget
            key={index}
            id={index}
            placedPiece={placedPieces[index]}
            onDrop={onDrop}
            onRemovePiece={onRemovePiece}   // ⬅️ passed down directly
          />
        ))}
      </PuzzleTargetsContainer>
    </BoardContainer>
  );
};

export default PuzzleBoard;